import type { ReactNode } from 'react';
import { ExternalLink } from 'lucide-react';
import type { ConsBusinessGroupDetail, KpiMode } from '../../types/consolidatedBusiness';
import { CB_STATUS_META, toCbStatus } from './status';
import { HUBSPOT_DEAL_BASE } from './constants';
import { fmtNum, fmtDate, ampel, AMPEL_LABEL, kpiUnit } from './format';

interface Props {
    detail: ConsBusinessGroupDetail;
    kpiMode: KpiMode;
}

const KPI_LABEL: Record<KpiMode, string> = {
    net: 'Net sales',
    quantity: 'Quantity',
    grossprofit: 'Gross profit',
};

/**
 * The combination's own figures — what replaced the batch-variant table (migration 132).
 *
 * The old table listed one row per ProductMasterID with its own check state. That grain was
 * never a business fact: it is how the source warehouse happens to store a product, and a
 * salesperson looking at "PVDF Kynar 720 natur" has no use for the eleven pack sizes it was
 * invoiced in. What they need is the combination's rhythm and its money, which is this grid.
 */
export function ConsBusinessGroupFacts({ detail, kpiMode }: Props) {
    const status = toCbStatus(detail.status);
    const meta = CB_STATUS_META[status];
    // No light when the value is unknown: ampel() reads a missing value as 0, which drew a
    // green dot beside a dash (design audit S1-07). The word stands beside the dot.
    const a = detail.daysSince === null || detail.daysSince === undefined ? null : ampel(detail.daysSince, detail.avgCycle);
    const dot = a === 'red' ? 'bg-red-600' : a === 'amber' ? 'bg-amber-500' : 'bg-green-600';
    const kpi = KPI_LABEL[kpiMode];
    const unit = `(${kpiUnit(kpiMode)})`;

    return (
        <div className="space-y-3">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
                <Fact label="Last invoice" value={fmtDate(detail.lastInvoice)} />
                <Fact
                    label="Days since"
                    value={
                        <span className="inline-flex items-center gap-1.5 tabular-nums">
                            {a && <span aria-hidden="true" className={`inline-block h-2 w-2 rounded-full ${dot}`} />}
                            {fmtNum(detail.daysSince)}
                            {a && <span className="font-normal text-gray-600">· {AMPEL_LABEL[a]}</span>}
                        </span>
                    }
                />
                <Fact label="Avg cycle (days)" value={fmtNum(detail.avgCycle)} />
                <Fact label={`${kpi} YTD ${unit}`} value={fmtNum(detail.kpiYTD)} />
                <Fact label={`${kpi} last 12 mts ${unit}`} value={fmtNum(detail.kpi12m)} />
                <Fact label={`${kpi} avg p. year ${unit}`} value={fmtNum(detail.kpiYearAvg)} />
                <Fact label="Supplier" value={detail.supplierDimName ?? '–'} />
                <Fact label="Subclass" value={detail.categoryName ?? '–'} />
                <Fact label="Business unit" value={detail.businessUnitCode ?? '–'} />
                <Fact label="Company" value={detail.companyCode ?? '–'} />
                <Fact
                    label="Review due"
                    value={detail.reviewDueAt ? fmtDate(detail.reviewDueAt) : 'no reminder'}
                />
                <Fact
                    label="Status"
                    value={
                        <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${meta.chipClass}`}>
                            {meta.label}
                        </span>
                    }
                />
            </dl>

            {detail.statusNote && (
                <div className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-700">Status note</p>
                    <p className="mt-0.5 whitespace-pre-wrap text-xs text-blue-900">{detail.statusNote}</p>
                </div>
            )}

            {detail.checkedBy && (
                <p className="text-[11px] text-gray-500">
                    Last checked by {detail.checkedBy}
                    {detail.checkedAt ? ` on ${fmtDate(detail.checkedAt)}` : ''}
                </p>
            )}

            {detail.deals.length > 0 && (
                <div>
                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        HubSpot deals
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {detail.deals.map(id => (
                            <a
                                key={id}
                                href={`${HUBSPOT_DEAL_BASE}${id}`}
                                target="_blank"
                                rel="noreferrer"
                                onClick={e => e.stopPropagation()}
                                className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 hover:bg-blue-100"
                            >
                                Deal {id}
                                <ExternalLink className="h-3 w-3" />
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function Fact({ label, value }: { label: string; value: ReactNode }) {
    return (
        <div>
            <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</dt>
            <dd className="text-xs font-medium text-gray-900">{value}</dd>
        </div>
    );
}
